import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize } from 'rxjs/operators';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

import { environment } from '../../../environments/environment';
import { NotificationService } from '../../services/notification.service';
import { ErrorHelperService } from '../../services/error-helper.service';
import { SearchableSelectComponent, SearchableSelectOption } from '../../components/searchable-select/searchable-select.component';

/**
 * Stati di picking, nell'ordine di visualizzazione:
 *  PRE_PLANNED        -> Pre-pickerabili (come pickerabili ma data arrivo navetta nulla)
 *  PLANNED            -> Pickerabili
 *  EXECUTED           -> Pickerati
 *  UNKNOWN_UNCERTAIN  -> Non pickerati incerti (nessun evento PTV successivo)
 *  UNKNOWN_DEFINITIVE -> Non pickerati definitivi (almeno un evento PTV successivo)
 */
export type PickingStatus = 'PRE_PLANNED' | 'PLANNED' | 'EXECUTED' | 'UNKNOWN_UNCERTAIN' | 'UNKNOWN_DEFINITIVE';

export const PICKING_STATUSES: PickingStatus[] = ['PRE_PLANNED', 'PLANNED', 'EXECUTED', 'UNKNOWN_UNCERTAIN', 'UNKNOWN_DEFINITIVE'];

/** Tipo viaggio, stessa regola del trip-management: isExternalTrip ? 'DELIVERY' : 'TRANSFER'. */
export type PickingTripType = 'TRANSFER' | 'DELIVERY';

export interface PickingTripTypeOption {
  value: PickingTripType;
  label: string;
  icon: string;
}

type PickingDateField = 'pickingDateFromStr' | 'pickingDateToStr'
  | 'shuttleArrivalDateFromStr' | 'shuttleArrivalDateToStr'
  | 'shuttleDepartureDateFromStr' | 'shuttleDepartureDateToStr';

export interface Warehouse {
  warehouseCode: string;
  warehouseName: string;
  warehouseShortName?: string;
}

export interface PickingParcel {
  parcelId?: number;
  parcelCode: string;
  parcelBarcode?: string;
  parcelComplementaryId?: number;
  tripId?: number;
  parcelComplementaryStatus?: string;
  parcelWeight?: number;
  parcelVolume?: number;
  parcelDescription?: string;
  parcelPalletType?: string;
  waybillCode?: string;
  clientCode?: string;
  clientName?: string;
  warehouseCode?: string;
  warehouseName?: string;
  warehouseShortName?: string;
  dealerCode?: string;
  dealerName?: string;
  dealerAddressStreet?: string;
  dealerAddressCity?: string;
  dealerAddressProvince?: string;
  dealerAddressZip?: string;
  eventDate?: string;
  scanned?: string;
  isOrphan?: boolean;
  pickingStatus?: PickingStatus;
  loadingListId?: number;
  loadingListCode?: string;
  loadingListStatus?: string;
  loadingListWarehouseCode?: string;
  loadingListTourGroups?: string;
  warehouseDirection?: 'DEPARTURE' | 'ARRIVAL';
  shuttleArrivalDate?: string;
  tripOriginWarehouses?: string;
  parcelOriginWarehouseShortName?: string;
  tripCode?: string;
  tripStatus?: string;
  tripCarrierCode?: string;
  tripCarrierName?: string;
  tripPlateNo?: string;
}

export interface PickingLoadingListGroup {
  loadingListId: number | null;
  loadingListCode: string;
  loadingListStatus?: string;
  loadingListWarehouseCode?: string;
  loadingListTourGroups?: string;
  direction?: 'DEPARTURE' | 'ARRIVAL';
  prePlannedCount: number;
  plannedCount: number;
  executedCount: number;
  unknownUncertainCount: number;
  unknownDefinitiveCount: number;
  unknownCount: number;
  totalCount: number;
  parcels: PickingParcel[];
}

export interface PickingTripGroup {
  tripId: number | null;
  tripCode: string;
  tripStatus?: string;
  tripCarrierCode?: string;
  tripCarrierName?: string;
  tripPlateNo?: string;
  tripCreatedAt?: string;
  prePlannedCount: number;
  plannedCount: number;
  executedCount: number;
  unknownUncertainCount: number;
  unknownDefinitiveCount: number;
  unknownCount: number;
  totalCount: number;
  parcels: PickingParcel[];
  loadingLists: PickingLoadingListGroup[];
  tripOriginWarehouses?: string;
  tripShuttleArrivalDate?: string;
}

export interface PickingWarehouseGroup {
  warehouseCode: string;
  warehouseName: string;
  warehouseShortName: string;
  prePlannedCount: number;
  plannedCount: number;
  executedCount: number;
  unknownUncertainCount: number;
  unknownDefinitiveCount: number;
  unknownCount: number;
  orphanCount: number;
  totalCount: number;
  trips: PickingTripGroup[];
  orphanParcels: PickingParcel[];
}

export interface PickingSummary {
  totalPrePlanned: number;
  totalPlanned: number;
  totalExecuted: number;
  totalUnknownUncertain: number;
  totalUnknownDefinitive: number;
  totalUnknown: number;
  totalOrphans: number;
}

const EMPTY_SUMMARY: PickingSummary = {
  totalPrePlanned: 0,
  totalPlanned: 0,
  totalExecuted: 0,
  totalUnknownUncertain: 0,
  totalUnknownDefinitive: 0,
  totalUnknown: 0,
  totalOrphans: 0
};

type TopSortField = 'warehouseName' | 'tripCode' | 'carrier' | 'plate'
  | 'prePlannedCount' | 'plannedCount' | 'executedCount' | 'executedPercent'
  | 'unknownUncertainCount' | 'unknownDefinitiveCount' | 'unknownCount'
  | 'totalCount' | 'orphanCount' | 'tripOriginWarehouses' | 'tripShuttleArrivalDate';

interface PickingSearchResponse {
  summary: PickingSummary;
  groups: PickingWarehouseGroup[];
  totalRecords: number;
  page: number;
  pageSize: number;
  // Valori completi (tutte le pagine) dei filtri a imbuto viaggio; presenti solo con magazzino selezionato
  tripOriginOptions?: string[] | null;
  tripArrivalDayOptions?: string[] | null;
}

@Component({
  selector: 'app-overview-picking',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule, SearchableSelectComponent],
  templateUrl: './overview-picking.component.html',
  styleUrls: ['./overview-picking.component.css']
})
export class OverviewPickingComponent implements OnInit {

  @ViewChild('pickingDateFromInput') pickingDateFromInput?: ElementRef<HTMLInputElement>;
  @ViewChild('pickingDateToInput') pickingDateToInput?: ElementRef<HTMLInputElement>;
  @ViewChild('shuttleArrivalDateFromInput') shuttleArrivalDateFromInput?: ElementRef<HTMLInputElement>;
  @ViewChild('shuttleArrivalDateToInput') shuttleArrivalDateToInput?: ElementRef<HTMLInputElement>;
  @ViewChild('shuttleDepartureDateFromInput') shuttleDepartureDateFromInput?: ElementRef<HTMLInputElement>;
  @ViewChild('shuttleDepartureDateToInput') shuttleDepartureDateToInput?: ElementRef<HTMLInputElement>;

  private datePickers = new Map<PickingDateField, FlatpickrInstance>();

  isLoading = false;
  showFilters = true;
  isReportCollapsed = false;

  warehouses: Warehouse[] = [];
  filterableWarehouses: Warehouse[] = [];
  warehouseCodes: string[] = [];

  selectedWarehouseCode: string | null = null;
  warehouseSelectOptions: SearchableSelectOption[] = [];

  searchTripCode = '';
  searchParcelCode = '';
  pickingDateFromStr = '';
  pickingDateToStr = '';
  shuttleArrivalDateFromStr = '';
  shuttleArrivalDateToStr = '';
  shuttleDepartureDateFromStr = '';
  shuttleDepartureDateToStr = '';
  searchWaybillCode = '';
  originWarehouseCode: string | null = null;
  originWarehouseSelectOptions: SearchableSelectOption[] = [];
  readonly pickingStatuses: PickingStatus[] = PICKING_STATUSES;
  statusFilterSelection: Set<PickingStatus> = new Set(PICKING_STATUSES);
  filterValidationError = false;

  readonly tripTypeOptions: PickingTripTypeOption[] = [
    { value: 'TRANSFER', label: 'Trasferimenti', icon: 'fa-right-left' },
    { value: 'DELIVERY', label: 'Consegne', icon: 'fa-truck' }
  ];
  // Selezione multipla (almeno uno): solo TRANSFER di default; entrambi = tutti i viaggi
  tripTypeSelection: Set<PickingTripType> = new Set<PickingTripType>(['TRANSFER']);

  summary: PickingSummary = { ...EMPTY_SUMMARY };
  groups: PickingWarehouseGroup[] = [];

  readonly pageSizeOptions: number[] = [5, 10, 25, 50, 100];
  currentPage = 1;
  itemsPerPage = 10;
  totalRecords = 0;

  expandedWarehouses = new Set<string>();
  expandedTrips = new Set<string>();
  expandedLoadingLists = new Set<string>();

  // Filtro a imbuto + ordinamento della tabella Piattaforme/Viaggi (livello di primo livello, paginato)
  platformFilter = '';
  tripCodeFilter = '';
  carrierFilter = '';
  plateFilter = '';
  platformValues: Set<string> = new Set();
  tripValues: Set<string> = new Set();
  carrierValues: Set<string> = new Set();
  plateValues: Set<string> = new Set();
  platformValueSearch = '';
  tripValueSearch = '';
  carrierValueSearch = '';
  plateValueSearch = '';
  topSort: { active: TopSortField | null; direction: 'asc' | 'desc' } = { active: null, direction: 'asc' };
  openTopFilterKey: string | null = null;
  topFilterPopoverStyle: { top: string; left: string } = { top: '0px', left: '0px' };

  // Filtro a imbuto (solo lato client, sulla pagina/gruppo attualmente caricato) per le 2 colonne aggiuntive della tabella Viaggio
  tripOriginFilter = '';
  tripOriginValues: Set<string> = new Set();
  tripOriginValueSearch = '';
  tripArrivalFilter = '';
  tripArrivalValues: Set<string> = new Set();
  tripArrivalValueSearch = '';
  // Opzioni restituite dal backend quando i viaggi sono paginati (magazzino selezionato): coprono tutte le pagine
  private serverTripOriginOptions: string[] | null = null;
  private serverTripArrivalDayOptions: string[] | null = null;

  // Filtro a imbuto + ordinamento della tabella Borderò (livello nidificato, sempre lato client)
  loadingListColumnFilters: { [key: string]: string } = {};
  loadingListValueFilters: { [key: string]: Set<string> } = {};
  loadingListValueSearch: { [key: string]: string } = {};
  loadingListSort: { active: string | null; direction: 'asc' | 'desc' } = { active: null, direction: 'asc' };
  openLoadingListFilterKey: string | null = null;
  loadingListFilterPopoverStyle: { top: string; left: string } = { top: '0px', left: '0px' };


  // Filtro a imbuto + ordinamento della tabella Colli (livello foglia, mai paginato: tutto lato client)
  parcelColumnFilters: { [key: string]: string } = {};
  parcelValueFilters: { [key: string]: Set<string> } = {};
  parcelValueSearch: { [key: string]: string } = {};
  parcelSort: { active: string | null; direction: 'asc' | 'desc' } = { active: null, direction: 'asc' };
  openParcelFilterKey: string | null = null;
  parcelFilterPopoverStyle: { top: string; left: string } = { top: '0px', left: '0px' };

  // Filtro a imbuto + ordinamento del popup "Pickerati senza affido" (sempre lato client)
  orphanColumnFilters: { [key: string]: string } = {};
  orphanValueFilters: { [key: string]: Set<string> } = {};
  orphanValueSearch: { [key: string]: string } = {};
  orphanSort: { active: string | null; direction: 'asc' | 'desc' } = { active: null, direction: 'asc' };
  openOrphanFilterKey: string | null = null;
  orphanFilterPopoverStyle: { top: string; left: string } = { top: '0px', left: '0px' };

  selectedParcelForModal: PickingParcel | null = null;
  showOrphansListPopup = false;
  statusListPopup: PickingStatus | 'ALL' | null = null;
  parcelDetailReturnsToStatusList = false;

  // Filtro a imbuto + ordinamento del popup "Pickerati / Pickerabili / Non Pickerati / Totale" (sempre lato client)
  statusListColumnFilters: { [key: string]: string } = {};
  statusListValueFilters: { [key: string]: Set<string> } = {};
  statusListValueSearch: { [key: string]: string } = {};
  statusListSort: { active: string | null; direction: 'asc' | 'desc' } = { active: null, direction: 'asc' };
  openStatusListFilterKey: string | null = null;
  statusListFilterPopoverStyle: { top: string; left: string } = { top: '0px', left: '0px' };

  hasSearched = false;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private errorHelperService: ErrorHelperService
  ) {}

  ngOnInit(): void {
    this.loadWarehouses();
  }

  get isSingleWarehouseUser(): boolean {
    return this.filterableWarehouses.length === 1;
  }

  get showPlatformGrouping(): boolean {
    return !this.isSingleWarehouseUser && !this.selectedWarehouseCode;
  }

  get selectedWarehouseName(): string {
    if (!this.isSingleWarehouseUser) return '';
    return this.filterableWarehouses[0]?.warehouseName || '';
  }

  loadWarehouses(): void {
    this.http.get<Warehouse[]>(`${environment.apiUrl}/overview-picking/warehouses`).subscribe({
      next: (data) => {
        this.warehouses = data || [];
        this.filterableWarehouses = data || [];

        const stored = localStorage.getItem('userFunctions');
        if (stored) {
          const parsed = JSON.parse(stored);
          this.warehouseCodes = parsed.warehouses;
          if (this.warehouseCodes != null && this.warehouseCodes.length === 1) {
            this.filterableWarehouses = this.filterableWarehouses.filter(w => this.warehouseCodes.includes(w.warehouseCode));
          }
        }

        this.warehouseSelectOptions = this.filterableWarehouses.map(w => ({
          value: w.warehouseCode,
          label: `${w.warehouseName}${w.warehouseShortName ? ' (' + w.warehouseShortName + ')' : ''}`
        }));

        if (this.isSingleWarehouseUser) {
          this.selectedWarehouseCode = this.filterableWarehouses[0].warehouseCode;
        }

        this.search();
      },
      error: (err) => {
        const message = this.errorHelperService.extractBackendMessage(err);
        this.notificationService.error(`Errore nel caricamento piattaforme: ${message}`);
      }
    });

    this.http.get<Warehouse[]>(`${environment.apiUrl}/overview-picking/warehouses-all`).subscribe({
      next: (data) => {
        this.originWarehouseSelectOptions = (data || []).map(w => ({
          value: w.warehouseCode,
          label: `${w.warehouseName}${w.warehouseShortName ? ' (' + w.warehouseShortName + ')' : ''}`
        }));
      },
      error: (err) => {
        const message = this.errorHelperService.extractBackendMessage(err);
        this.notificationService.error(`Errore nel caricamento magazzini di partenza: ${message}`);
      }
    });
  }

  onWarehouseChange(): void {
    this.currentPage = 1;
    this.expandedWarehouses.clear();
    this.expandedTrips.clear();
    this.expandedLoadingLists.clear();
    this.search();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.search();
  }

  hasRequiredAnchorFilter(): boolean {
    return !!this.searchTripCode?.trim()
      || !!this.searchWaybillCode?.trim()
      || !!this.searchParcelCode?.trim()
      || (!!this.pickingDateFromStr && !!this.pickingDateToStr)
      || (!!this.shuttleArrivalDateFromStr && !!this.shuttleArrivalDateToStr)
      || (!!this.shuttleDepartureDateFromStr && !!this.shuttleDepartureDateToStr);
  }

  isTripTypeChecked(tripType: PickingTripType): boolean {
    return this.tripTypeSelection.has(tripType);
  }

  toggleTripTypeFilter(tripType: PickingTripType): void {
    if (this.tripTypeSelection.has(tripType)) {
      if (this.tripTypeSelection.size === 1) {
        return; // almeno un tipo viaggio deve restare selezionato
      }
      this.tripTypeSelection.delete(tripType);
    } else {
      this.tripTypeSelection.add(tripType);
    }
  }

  /** Valore inviato al backend: null = tutti i tipi (entrambi selezionati). */
  private getTripTypeParam(): PickingTripType | null {
    return this.tripTypeSelection.size === 1 ? Array.from(this.tripTypeSelection)[0] : null;
  }

  isStatusFilterChecked(status: PickingStatus): boolean {
    return this.statusFilterSelection.has(status);
  }

  toggleStatusFilter(status: PickingStatus): void {
    if (this.statusFilterSelection.has(status)) {
      if (this.statusFilterSelection.size === 1) {
        return; // almeno uno stato deve restare selezionato
      }
      this.statusFilterSelection.delete(status);
    } else {
      this.statusFilterSelection.add(status);
    }
  }

  resetFilters(): void {
    this.searchTripCode = '';
    this.searchParcelCode = '';
    this.searchWaybillCode = '';
    this.pickingDateFromStr = '';
    this.pickingDateToStr = '';
    this.shuttleArrivalDateFromStr = '';
    this.shuttleArrivalDateToStr = '';
    this.shuttleDepartureDateFromStr = '';
    this.shuttleDepartureDateToStr = '';
    this.originWarehouseCode = null;
    this.statusFilterSelection = new Set(PICKING_STATUSES);
    this.tripTypeSelection = new Set<PickingTripType>(['TRANSFER']);
    this.platformFilter = '';
    this.tripCodeFilter = '';
    this.carrierFilter = '';
    this.topSort = { active: null, direction: 'asc' };
    if (!this.isSingleWarehouseUser) {
      this.selectedWarehouseCode = null;
    }
    this.currentPage = 1;
    this.expandedWarehouses.clear();
    this.expandedTrips.clear();
    this.expandedLoadingLists.clear();
    this.search();
  }

  // ===== Filtro/ordinamento tabella Piattaforme / Viaggi (top-level, va al backend per la paginazione) =====

  private positionPopover(event: MouseEvent | undefined, apply: (style: { top: string; left: string }) => void): void {
    if (!event) {
      apply({ top: '0px', left: '0px' });
      return;
    }
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const popoverWidth = 280;
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - 16) {
      left = Math.max(16, window.innerWidth - popoverWidth - 16);
    }
    apply({ top: `${rect.bottom + 6}px`, left: `${left}px` });
  }

  toggleTopFilterPopover(key: string, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.openTopFilterKey === key) {
      this.openTopFilterKey = null;
      return;
    }
    this.openTopFilterKey = key;
    this.positionPopover(event, (style) => (this.topFilterPopoverStyle = style));
  }

  closeTopFilterPopover(): void {
    this.openTopFilterKey = null;
  }

  isTopFilterActive(key: 'platform' | 'trip' | 'carrier' | 'plate'): boolean {
    if (key === 'platform') return !!this.platformFilter || this.platformValues.size > 0;
    if (key === 'trip') return !!this.tripCodeFilter || this.tripValues.size > 0;
    if (key === 'plate') return !!this.plateFilter || this.plateValues.size > 0;
    return !!this.carrierFilter || this.carrierValues.size > 0;
  }

  applyTopFilterPopover(): void {
    this.openTopFilterKey = null;
    this.currentPage = 1;
    this.search();
  }

  clearTopFilter(key: 'platform' | 'trip' | 'carrier' | 'plate'): void {
    if (key === 'platform') { this.platformFilter = ''; this.platformValues.clear(); this.platformValueSearch = ''; }
    if (key === 'trip') { this.tripCodeFilter = ''; this.tripValues.clear(); this.tripValueSearch = ''; }
    if (key === 'carrier') { this.carrierFilter = ''; this.carrierValues.clear(); this.carrierValueSearch = ''; }
    if (key === 'plate') { this.plateFilter = ''; this.plateValues.clear(); this.plateValueSearch = ''; }
    this.openTopFilterKey = null;
    this.currentPage = 1;
    this.search();
  }

  isTopValueFilterActive(key: 'platform' | 'trip' | 'carrier' | 'plate'): boolean {
    if (key === 'platform') return this.platformValues.size > 0;
    if (key === 'trip') return this.tripValues.size > 0;
    if (key === 'plate') return this.plateValues.size > 0;
    return this.carrierValues.size > 0;
  }

  private getAllLoadedTrips(): PickingTripGroup[] {
    const result: PickingTripGroup[] = [];
    for (const g of this.groups) {
      result.push(...(g.trips || []));
    }
    return result;
  }

  getPlatformValueOptions(): string[] {
    const values = new Set<string>();
    for (const g of this.groups) {
      if (g.warehouseName) values.add(g.warehouseName);
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.platformValueSearch?.trim().toUpperCase();
    if (search) options = options.filter(o => o.toUpperCase().includes(search));
    return options;
  }

  getTripValueOptions(): string[] {
    const values = new Set<string>();
    for (const t of this.getAllLoadedTrips()) {
      if (t.tripCode) values.add(t.tripCode);
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.tripValueSearch?.trim().toUpperCase();
    if (search) options = options.filter(o => o.toUpperCase().includes(search));
    return options;
  }

  getCarrierValueOptions(): string[] {
    const values = new Set<string>();
    for (const t of this.getAllLoadedTrips()) {
      if (t.tripCarrierName) values.add(t.tripCarrierName);
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.carrierValueSearch?.trim().toUpperCase();
    if (search) options = options.filter(o => o.toUpperCase().includes(search));
    return options;
  }

  getPlateValueOptions(): string[] {
    const values = new Set<string>();
    for (const t of this.getAllLoadedTrips()) {
      if (t.tripPlateNo) values.add(t.tripPlateNo);
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.plateValueSearch?.trim().toUpperCase();
    if (search) options = options.filter(o => o.toUpperCase().includes(search));
    return options;
  }

  isTripOriginFilterActive(): boolean {
    return !!this.tripOriginFilter || this.tripOriginValues.size > 0;
  }

  clearTripOriginFilter(): void {
    this.tripOriginFilter = '';
    this.tripOriginValues.clear();
    this.tripOriginValueSearch = '';
    this.openTopFilterKey = null;
  }

  getTripOriginValueOptions(): string[] {
    if (this.serverTripOriginOptions) {
      const search = this.tripOriginValueSearch?.trim().toUpperCase();
      return search ? this.serverTripOriginOptions.filter(o => o.toUpperCase().includes(search)) : this.serverTripOriginOptions;
    }
    const values = new Set<string>();
    for (const t of this.getAllLoadedTrips()) {
      if (t.tripOriginWarehouses) values.add(t.tripOriginWarehouses);
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.tripOriginValueSearch?.trim().toUpperCase();
    if (search) options = options.filter(o => o.toUpperCase().includes(search));
    return options;
  }

  isTripOriginValueChecked(value: string): boolean {
    return this.tripOriginValues.has(value);
  }

  toggleTripOriginValue(value: string): void {
    if (this.tripOriginValues.has(value)) {
      this.tripOriginValues.delete(value);
    } else {
      this.tripOriginValues.add(value);
    }
  }

  areAllTripOriginValuesChecked(): boolean {
    const options = this.getTripOriginValueOptions();
    return options.length > 0 && options.every(o => this.tripOriginValues.has(o));
  }

  toggleAllTripOriginValues(): void {
    const options = this.getTripOriginValueOptions();
    if (this.areAllTripOriginValuesChecked()) {
      options.forEach(o => this.tripOriginValues.delete(o));
    } else {
      options.forEach(o => this.tripOriginValues.add(o));
    }
  }

  isTripArrivalFilterActive(): boolean {
    return !!this.tripArrivalFilter || this.tripArrivalValues.size > 0;
  }

  clearTripArrivalFilter(): void {
    this.tripArrivalFilter = '';
    this.tripArrivalValues.clear();
    this.tripArrivalValueSearch = '';
    this.openTopFilterKey = null;
  }

  getTripArrivalValueOptions(): string[] {
    if (this.serverTripArrivalDayOptions) {
      const search = this.tripArrivalValueSearch?.trim().toUpperCase();
      return search ? this.serverTripArrivalDayOptions.filter(o => o.toUpperCase().includes(search)) : this.serverTripArrivalDayOptions;
    }
    const values = new Set<string>();
    for (const t of this.getAllLoadedTrips()) {
      const v = this.formatFilterDay(t.tripShuttleArrivalDate);
      if (v) values.add(v);
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.tripArrivalValueSearch?.trim().toUpperCase();
    if (search) options = options.filter(o => o.toUpperCase().includes(search));
    return options;
  }

  isTripArrivalValueChecked(value: string): boolean {
    return this.tripArrivalValues.has(value);
  }

  toggleTripArrivalValue(value: string): void {
    if (this.tripArrivalValues.has(value)) {
      this.tripArrivalValues.delete(value);
    } else {
      this.tripArrivalValues.add(value);
    }
  }

  areAllTripArrivalValuesChecked(): boolean {
    const options = this.getTripArrivalValueOptions();
    return options.length > 0 && options.every(o => this.tripArrivalValues.has(o));
  }

  toggleAllTripArrivalValues(): void {
    const options = this.getTripArrivalValueOptions();
    if (this.areAllTripArrivalValuesChecked()) {
      options.forEach(o => this.tripArrivalValues.delete(o));
    } else {
      options.forEach(o => this.tripArrivalValues.add(o));
    }
  }

  private topValueSet(key: 'platform' | 'trip' | 'carrier' | 'plate'): Set<string> {
    switch (key) {
      case 'platform': return this.platformValues;
      case 'trip': return this.tripValues;
      case 'plate': return this.plateValues;
      default: return this.carrierValues;
    }
  }

  private topValueOptions(key: 'platform' | 'trip' | 'carrier' | 'plate'): string[] {
    switch (key) {
      case 'platform': return this.getPlatformValueOptions();
      case 'trip': return this.getTripValueOptions();
      case 'plate': return this.getPlateValueOptions();
      default: return this.getCarrierValueOptions();
    }
  }

  isTopValueChecked(key: 'platform' | 'trip' | 'carrier' | 'plate', value: string): boolean {
    return this.topValueSet(key).has(value);
  }

  toggleTopValue(key: 'platform' | 'trip' | 'carrier' | 'plate', value: string): void {
    const set = this.topValueSet(key);
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
  }

  areAllTopValuesChecked(key: 'platform' | 'trip' | 'carrier' | 'plate'): boolean {
    const options = this.topValueOptions(key);
    const set = this.topValueSet(key);
    return options.length > 0 && options.every(o => set.has(o));
  }

  toggleAllTopValues(key: 'platform' | 'trip' | 'carrier' | 'plate'): void {
    const options = this.topValueOptions(key);
    const set = this.topValueSet(key);
    if (this.areAllTopValuesChecked(key)) {
      options.forEach(o => set.delete(o));
    } else {
      options.forEach(o => set.add(o));
    }
  }

  setTopSort(field: TopSortField): void {
    if (this.topSort.active === field) {
      this.topSort.direction = this.topSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.topSort.active = field;
      this.topSort.direction = 'asc';
    }
    this.currentPage = 1;
    this.search();
  }

  getTopSortIcon(field: string): string {
    if (this.topSort.active !== field) return 'fa-sort';
    return this.topSort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down';
  }

  hasActiveTableFiltersOrSort(): boolean {
    return !!this.platformFilter || !!this.tripCodeFilter || !!this.carrierFilter || !!this.plateFilter
      || this.platformValues.size > 0 || this.tripValues.size > 0 || this.carrierValues.size > 0 || this.plateValues.size > 0
      || this.isTripOriginFilterActive() || this.isTripArrivalFilterActive()
      || this.topSort.active !== null
      || Object.keys(this.parcelColumnFilters).length > 0
      || Object.values(this.parcelValueFilters).some(set => set && set.size > 0)
      || this.parcelSort.active !== null
      || Object.keys(this.orphanColumnFilters).length > 0
      || Object.values(this.orphanValueFilters).some(set => set && set.size > 0)
      || this.orphanSort.active !== null
      || Object.keys(this.loadingListColumnFilters).length > 0
      || Object.values(this.loadingListValueFilters).some(set => set && set.size > 0)
      || this.loadingListSort.active !== null;
  }

  resetTableFiltersAndSort(): void {
    this.platformFilter = '';
    this.tripCodeFilter = '';
    this.carrierFilter = '';
    this.plateFilter = '';
    this.platformValues.clear();
    this.tripValues.clear();
    this.carrierValues.clear();
    this.plateValues.clear();
    this.platformValueSearch = '';
    this.tripValueSearch = '';
    this.carrierValueSearch = '';
    this.plateValueSearch = '';
    this.clearTripOriginFilter();
    this.clearTripArrivalFilter();
    this.topSort = { active: null, direction: 'asc' };
    this.openTopFilterKey = null;

    this.parcelColumnFilters = {};
    this.parcelValueFilters = {};
    this.parcelValueSearch = {};
    this.parcelSort = { active: null, direction: 'asc' };
    this.openParcelFilterKey = null;

    this.orphanColumnFilters = {};
    this.orphanValueFilters = {};
    this.orphanValueSearch = {};
    this.orphanSort = { active: null, direction: 'asc' };
    this.openOrphanFilterKey = null;

    this.loadingListColumnFilters = {};
    this.loadingListValueFilters = {};
    this.loadingListValueSearch = {};
    this.loadingListSort = { active: null, direction: 'asc' };
    this.openLoadingListFilterKey = null;

    this.currentPage = 1;
    this.search();
  }

  // Livello nidificato (viaggi dentro una piattaforma espansa): mai paginato, filtro/ordinamento lato client
  // riusando lo stesso stato/testo digitato per coerenza con la tabella di primo livello.
  getFilteredSortedTrips(trips: PickingTripGroup[]): PickingTripGroup[] {
    let result = trips || [];

    if (this.tripCodeFilter?.trim()) {
      const f = this.tripCodeFilter.trim().toUpperCase();
      result = result.filter(t => (t.tripCode || '').toUpperCase().includes(f));
    }
    if (this.carrierFilter?.trim()) {
      const f = this.carrierFilter.trim().toUpperCase();
      result = result.filter(t => (t.tripCarrierName || '').toUpperCase().includes(f));
    }
    if (this.plateFilter?.trim()) {
      const f = this.plateFilter.trim().toUpperCase();
      result = result.filter(t => (t.tripPlateNo || '').toUpperCase().includes(f));
    }
    if (this.tripValues.size > 0) {
      result = result.filter(t => t.tripCode && this.tripValues.has(t.tripCode));
    }
    if (this.carrierValues.size > 0) {
      result = result.filter(t => t.tripCarrierName && this.carrierValues.has(t.tripCarrierName));
    }
    if (this.plateValues.size > 0) {
      result = result.filter(t => t.tripPlateNo && this.plateValues.has(t.tripPlateNo));
    }
    if (this.tripOriginFilter?.trim()) {
      const f = this.tripOriginFilter.trim().toUpperCase();
      result = result.filter(t => (t.tripOriginWarehouses || '').toUpperCase().includes(f));
    }
    if (this.tripOriginValues.size > 0) {
      result = result.filter(t => this.tripOriginValues.has(t.tripOriginWarehouses || ''));
    }
    if (this.tripArrivalFilter?.trim()) {
      const f = this.tripArrivalFilter.trim().toUpperCase();
      result = result.filter(t => this.formatFilterDay(t.tripShuttleArrivalDate).toUpperCase().includes(f));
    }
    if (this.tripArrivalValues.size > 0) {
      result = result.filter(t => this.tripArrivalValues.has(this.formatFilterDay(t.tripShuttleArrivalDate)));
    }

    if (this.topSort.active === 'tripCode' || this.topSort.active === 'carrier' || this.topSort.active === 'plate') {
      const dir = this.topSort.direction === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        const field = this.topSort.active;
        const va = field === 'tripCode' ? (a.tripCode || '') : field === 'carrier' ? (a.tripCarrierName || '') : (a.tripPlateNo || '');
        const vb = field === 'tripCode' ? (b.tripCode || '') : field === 'carrier' ? (b.tripCarrierName || '') : (b.tripPlateNo || '');
        return va.localeCompare(vb) * dir;
      });
    } else if (this.topSort.active === 'tripOriginWarehouses') {
      const dir = this.topSort.direction === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => (a.tripOriginWarehouses || '').localeCompare(b.tripOriginWarehouses || '') * dir);
    } else if (this.topSort.active === 'tripShuttleArrivalDate') {
      const dir = this.topSort.direction === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        const ta = a.tripShuttleArrivalDate ? new Date(a.tripShuttleArrivalDate).getTime() : 0;
        const tb = b.tripShuttleArrivalDate ? new Date(b.tripShuttleArrivalDate).getTime() : 0;
        return (ta - tb) * dir;
      });
    } else if (this.topSort.active === 'prePlannedCount' || this.topSort.active === 'plannedCount'
        || this.topSort.active === 'executedCount' || this.topSort.active === 'unknownUncertainCount'
        || this.topSort.active === 'unknownDefinitiveCount' || this.topSort.active === 'unknownCount'
        || this.topSort.active === 'totalCount') {
      const dir = this.topSort.direction === 'asc' ? 1 : -1;
      const field = this.topSort.active;
      result = [...result].sort((a, b) => ((a as any)[field] - (b as any)[field]) * dir);
    } else if (this.topSort.active === 'executedPercent') {
      const dir = this.topSort.direction === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        const pa = a.totalCount > 0 ? a.executedCount / a.totalCount : 0;
        const pb = b.totalCount > 0 ? b.executedCount / b.totalCount : 0;
        return (pa - pb) * dir;
      });
    }

    return result;
  }

  // ===== Filtro/ordinamento tabella Colli (livello foglia, mai paginato: sempre lato client) =====

  toggleParcelFilterPopover(key: string, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.openParcelFilterKey === key) {
      this.openParcelFilterKey = null;
      return;
    }
    this.openParcelFilterKey = key;
    this.positionPopover(event, (style) => (this.parcelFilterPopoverStyle = style));
  }

  closeParcelFilterPopover(): void {
    this.openParcelFilterKey = null;
  }

  isParcelFilterActive(key: string): boolean {
    return !!this.parcelColumnFilters[key] || (this.parcelValueFilters[key]?.size ?? 0) > 0;
  }

  applyParcelFilterPopover(): void {
    this.openParcelFilterKey = null;
  }

  clearParcelFilter(key: string): void {
    delete this.parcelColumnFilters[key];
    delete this.parcelValueFilters[key];
    delete this.parcelValueSearch[key];
    this.openParcelFilterKey = null;
  }

  isParcelValueFilterActive(key: string): boolean {
    return (this.parcelValueFilters[key]?.size ?? 0) > 0;
  }

  getParcelValueOptions(field: string): string[] {
    const values = new Set<string>();
    for (const g of this.groups) {
      for (const t of g.trips || []) {
        for (const p of t.parcels || []) {
          const v = this.parcelDisplayValue(p, field);
          if (v) values.add(v);
        }
      }
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.parcelValueSearch[field]?.trim().toUpperCase();
    if (search) {
      options = options.filter(o => o.toUpperCase().includes(search));
    }
    return options;
  }

  isParcelValueChecked(field: string, value: string): boolean {
    return this.parcelValueFilters[field]?.has(value) ?? false;
  }

  toggleParcelValue(field: string, value: string): void {
    if (!this.parcelValueFilters[field]) {
      this.parcelValueFilters[field] = new Set<string>();
    }
    const set = this.parcelValueFilters[field];
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
  }

  areAllParcelValuesChecked(field: string): boolean {
    const options = this.getParcelValueOptions(field);
    const set = this.parcelValueFilters[field];
    return options.length > 0 && !!set && options.every(o => set.has(o));
  }

  toggleAllParcelValues(field: string): void {
    const options = this.getParcelValueOptions(field);
    if (!this.parcelValueFilters[field]) {
      this.parcelValueFilters[field] = new Set<string>();
    }
    const set = this.parcelValueFilters[field];
    if (this.areAllParcelValuesChecked(field)) {
      options.forEach(o => set.delete(o));
    } else {
      options.forEach(o => set.add(o));
    }
  }

  setParcelSort(field: string): void {
    if (this.parcelSort.active === field) {
      this.parcelSort.direction = this.parcelSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.parcelSort.active = field;
      this.parcelSort.direction = 'asc';
    }
  }

  getParcelSortIcon(field: string): string {
    if (this.parcelSort.active !== field) return 'fa-sort';
    return this.parcelSort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down';
  }

  // Valore "grezzo" usato solo per l'ordinamento (numerico dove ha senso confrontare numericamente)
  private parcelFieldValue(p: PickingParcel, field: string): any {
    switch (field) {
      case 'parcelCode': return p.parcelCode || p.parcelBarcode || '';
      case 'eventDate': return p.eventDate ? new Date(p.eventDate).getTime() : 0;
      case 'pickingStatus': return this.getPickingStatusLabel(p.pickingStatus);
      case 'clientName': return p.clientName || '';
      case 'waybillCode': return p.waybillCode || '';
      case 'parcelWeight': return p.parcelWeight ?? 0;
      case 'parcelVolume': return p.parcelVolume ?? 0;
      case 'parcelDescription': return `${p.parcelDescription || ''} ${p.parcelPalletType || ''}`;
      case 'parcelOriginWarehouseShortName': return p.parcelOriginWarehouseShortName || '';
      default: return '';
    }
  }

  // Valore "visualizzato" usato per il filtro testo, per le checkbox e come opzioni della lista valori
  private parcelDisplayValue(p: PickingParcel, field: string): string {
    switch (field) {
      case 'parcelCode': return p.parcelCode || p.parcelBarcode || '';
      case 'eventDate': return this.formatFilterDay(p.eventDate);
      case 'pickingStatus': return this.getPickingStatusLabel(p.pickingStatus);
      case 'clientName': return p.clientName || '';
      case 'waybillCode': return p.waybillCode || '';
      case 'parcelWeight': return p.parcelWeight != null ? String(p.parcelWeight) : '';
      case 'parcelVolume': return p.parcelVolume != null ? String(p.parcelVolume) : '';
      case 'parcelDescription': {
        const desc = p.parcelDescription || '';
        const pallet = p.parcelPalletType || '';
        return desc && pallet ? `${desc} - ${pallet}` : (desc || pallet);
      }
      case 'parcelOriginWarehouseShortName': return p.parcelOriginWarehouseShortName || '';
      default: return '';
    }
  }

  getFilteredSortedParcels(parcels: PickingParcel[]): PickingParcel[] {
    let result = parcels || [];

    for (const key of Object.keys(this.parcelColumnFilters)) {
      const filterValue = this.parcelColumnFilters[key]?.trim();
      if (!filterValue) continue;
      const f = filterValue.toUpperCase();
      result = result.filter(p => this.parcelDisplayValue(p, key).toUpperCase().includes(f));
    }

    for (const key of Object.keys(this.parcelValueFilters)) {
      const set = this.parcelValueFilters[key];
      if (!set || set.size === 0) continue;
      result = result.filter(p => set.has(this.parcelDisplayValue(p, key)));
    }

    if (this.parcelSort.active) {
      const dir = this.parcelSort.direction === 'asc' ? 1 : -1;
      const field = this.parcelSort.active;
      result = [...result].sort((a, b) => {
        const va = this.parcelFieldValue(a, field);
        const vb = this.parcelFieldValue(b, field);
        if (typeof va === 'number' && typeof vb === 'number') {
          return (va - vb) * dir;
        }
        return String(va).localeCompare(String(vb)) * dir;
      });
    }

    return result;
  }

  // ===== Filtro/ordinamento tabella Borderò (sempre nidificata, sempre lato client) =====

  toggleLoadingListFilterPopover(key: string, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.openLoadingListFilterKey === key) {
      this.openLoadingListFilterKey = null;
      return;
    }
    this.openLoadingListFilterKey = key;
    this.positionPopover(event, (style) => (this.loadingListFilterPopoverStyle = style));
  }

  closeLoadingListFilterPopover(): void {
    this.openLoadingListFilterKey = null;
  }

  isLoadingListFilterActive(key: string): boolean {
    return !!this.loadingListColumnFilters[key] || (this.loadingListValueFilters[key]?.size ?? 0) > 0;
  }

  applyLoadingListFilterPopover(): void {
    this.openLoadingListFilterKey = null;
  }

  clearLoadingListFilter(key: string): void {
    delete this.loadingListColumnFilters[key];
    delete this.loadingListValueFilters[key];
    delete this.loadingListValueSearch[key];
    this.openLoadingListFilterKey = null;
  }

  setLoadingListSort(field: string): void {
    if (this.loadingListSort.active === field) {
      this.loadingListSort.direction = this.loadingListSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.loadingListSort.active = field;
      this.loadingListSort.direction = 'asc';
    }
  }

  getLoadingListSortIcon(field: string): string {
    if (this.loadingListSort.active !== field) return 'fa-sort';
    return this.loadingListSort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down';
  }

  private loadingListDisplayValue(ll: PickingLoadingListGroup, field: string): string {
    switch (field) {
      case 'direction': return this.getLoadingListDirectionLabel(ll);
      case 'loadingListTourGroups': return ll.loadingListTourGroups || '';
      default: return '';
    }
  }

  getLoadingListValueOptions(loadingLists: PickingLoadingListGroup[], field: string): string[] {
    const values = new Set<string>();
    for (const ll of loadingLists || []) {
      const v = this.loadingListDisplayValue(ll, field);
      if (v) values.add(v);
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.loadingListValueSearch[field]?.trim().toUpperCase();
    if (search) options = options.filter(o => o.toUpperCase().includes(search));
    return options;
  }

  isLoadingListValueChecked(field: string, value: string): boolean {
    return this.loadingListValueFilters[field]?.has(value) ?? false;
  }

  toggleLoadingListValue(field: string, value: string): void {
    if (!this.loadingListValueFilters[field]) {
      this.loadingListValueFilters[field] = new Set<string>();
    }
    const set = this.loadingListValueFilters[field];
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
  }

  areAllLoadingListValuesChecked(loadingLists: PickingLoadingListGroup[], field: string): boolean {
    const options = this.getLoadingListValueOptions(loadingLists, field);
    const set = this.loadingListValueFilters[field];
    return options.length > 0 && !!set && options.every(o => set.has(o));
  }

  toggleAllLoadingListValues(loadingLists: PickingLoadingListGroup[], field: string): void {
    const options = this.getLoadingListValueOptions(loadingLists, field);
    if (!this.loadingListValueFilters[field]) {
      this.loadingListValueFilters[field] = new Set<string>();
    }
    const set = this.loadingListValueFilters[field];
    if (this.areAllLoadingListValuesChecked(loadingLists, field)) {
      options.forEach(o => set.delete(o));
    } else {
      options.forEach(o => set.add(o));
    }
  }

  getFilteredSortedLoadingLists(loadingLists: PickingLoadingListGroup[]): PickingLoadingListGroup[] {
    let result = loadingLists || [];

    for (const key of Object.keys(this.loadingListColumnFilters)) {
      const filterValue = this.loadingListColumnFilters[key]?.trim();
      if (!filterValue) continue;
      const f = filterValue.toUpperCase();
      result = result.filter(ll => this.loadingListDisplayValue(ll, key).toUpperCase().includes(f));
    }

    for (const key of Object.keys(this.loadingListValueFilters)) {
      const set = this.loadingListValueFilters[key];
      if (!set || set.size === 0) continue;
      result = result.filter(ll => set.has(this.loadingListDisplayValue(ll, key)));
    }

    if (this.loadingListSort.active) {
      const dir = this.loadingListSort.direction === 'asc' ? 1 : -1;
      const field = this.loadingListSort.active;
      result = [...result].sort((a, b) => {
        const va = this.loadingListDisplayValue(a, field);
        const vb = this.loadingListDisplayValue(b, field);
        return va.localeCompare(vb) * dir;
      });
    }

    return result;
  }

  // ===== Filtro/ordinamento popup "Pickerati senza affido" (sempre lato client) =====

  toggleOrphanFilterPopover(key: string, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.openOrphanFilterKey === key) {
      this.openOrphanFilterKey = null;
      return;
    }
    this.openOrphanFilterKey = key;
    this.positionPopover(event, (style) => (this.orphanFilterPopoverStyle = style));
  }

  closeOrphanFilterPopover(): void {
    this.openOrphanFilterKey = null;
  }

  isOrphanFilterActive(key: string): boolean {
    return !!this.orphanColumnFilters[key] || (this.orphanValueFilters[key]?.size ?? 0) > 0;
  }

  applyOrphanFilterPopover(): void {
    this.openOrphanFilterKey = null;
  }

  clearOrphanFilter(key: string): void {
    delete this.orphanColumnFilters[key];
    delete this.orphanValueFilters[key];
    delete this.orphanValueSearch[key];
    this.openOrphanFilterKey = null;
  }

  setOrphanSort(field: string): void {
    if (this.orphanSort.active === field) {
      this.orphanSort.direction = this.orphanSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.orphanSort.active = field;
      this.orphanSort.direction = 'asc';
    }
  }

  getOrphanSortIcon(field: string): string {
    if (this.orphanSort.active !== field) return 'fa-sort';
    return this.orphanSort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down';
  }

  private orphanFieldValue(p: PickingParcel, field: string): any {
    switch (field) {
      case 'parcelBarcode': return p.parcelBarcode || p.parcelCode || '';
      case 'warehouseName': return p.warehouseName || '';
      case 'eventDate': return p.eventDate ? new Date(p.eventDate).getTime() : 0;
      default: return '';
    }
  }

  private orphanDisplayValue(p: PickingParcel, field: string): string {
    switch (field) {
      case 'parcelBarcode': return p.parcelBarcode || p.parcelCode || '';
      case 'warehouseName': return p.warehouseName || '';
      case 'eventDate': return this.formatFilterDay(p.eventDate);
      default: return '';
    }
  }

  getOrphanValueOptions(field: string): string[] {
    const values = new Set<string>();
    for (const p of this.allOrphanParcels) {
      const v = this.orphanDisplayValue(p, field);
      if (v) values.add(v);
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.orphanValueSearch[field]?.trim().toUpperCase();
    if (search) {
      options = options.filter(o => o.toUpperCase().includes(search));
    }
    return options;
  }

  isOrphanValueChecked(field: string, value: string): boolean {
    return this.orphanValueFilters[field]?.has(value) ?? false;
  }

  toggleOrphanValue(field: string, value: string): void {
    if (!this.orphanValueFilters[field]) {
      this.orphanValueFilters[field] = new Set<string>();
    }
    const set = this.orphanValueFilters[field];
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
  }

  areAllOrphanValuesChecked(field: string): boolean {
    const options = this.getOrphanValueOptions(field);
    const set = this.orphanValueFilters[field];
    return options.length > 0 && !!set && options.every(o => set.has(o));
  }

  toggleAllOrphanValues(field: string): void {
    const options = this.getOrphanValueOptions(field);
    if (!this.orphanValueFilters[field]) {
      this.orphanValueFilters[field] = new Set<string>();
    }
    const set = this.orphanValueFilters[field];
    if (this.areAllOrphanValuesChecked(field)) {
      options.forEach(o => set.delete(o));
    } else {
      options.forEach(o => set.add(o));
    }
  }

  getFilteredSortedOrphans(): PickingParcel[] {
    let result = this.allOrphanParcels;

    for (const key of Object.keys(this.orphanColumnFilters)) {
      const filterValue = this.orphanColumnFilters[key]?.trim();
      if (!filterValue) continue;
      const f = filterValue.toUpperCase();
      result = result.filter(p => this.orphanDisplayValue(p, key).toUpperCase().includes(f));
    }

    for (const key of Object.keys(this.orphanValueFilters)) {
      const set = this.orphanValueFilters[key];
      if (!set || set.size === 0) continue;
      result = result.filter(p => set.has(this.orphanDisplayValue(p, key)));
    }

    if (this.orphanSort.active) {
      const dir = this.orphanSort.direction === 'asc' ? 1 : -1;
      const field = this.orphanSort.active;
      result = [...result].sort((a, b) => {
        const va = this.orphanFieldValue(a, field);
        const vb = this.orphanFieldValue(b, field);
        if (typeof va === 'number' && typeof vb === 'number') {
          return (va - vb) * dir;
        }
        return String(va).localeCompare(String(vb)) * dir;
      });
    }

    return result;
  }

  // ===== Filtro/ordinamento popup "Pickerati / Pickerabili / Non Pickerati / Totale" (sempre lato client) =====

  toggleStatusListFilterPopover(key: string, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.openStatusListFilterKey === key) {
      this.openStatusListFilterKey = null;
      return;
    }
    this.openStatusListFilterKey = key;
    this.positionPopover(event, (style) => (this.statusListFilterPopoverStyle = style));
  }

  closeStatusListFilterPopover(): void {
    this.openStatusListFilterKey = null;
  }

  isStatusListFilterActive(key: string): boolean {
    return !!this.statusListColumnFilters[key] || (this.statusListValueFilters[key]?.size ?? 0) > 0;
  }

  applyStatusListFilterPopover(): void {
    this.openStatusListFilterKey = null;
  }

  clearStatusListFilter(key: string): void {
    delete this.statusListColumnFilters[key];
    delete this.statusListValueFilters[key];
    delete this.statusListValueSearch[key];
    this.openStatusListFilterKey = null;
  }

  setStatusListSort(field: string): void {
    if (this.statusListSort.active === field) {
      this.statusListSort.direction = this.statusListSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.statusListSort.active = field;
      this.statusListSort.direction = 'asc';
    }
  }

  getStatusListSortIcon(field: string): string {
    if (this.statusListSort.active !== field) return 'fa-sort';
    return this.statusListSort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down';
  }

  private statusListFieldValue(p: PickingParcel, field: string): any {
    switch (field) {
      case 'parcelCode': return p.parcelCode || p.parcelBarcode || '';
      case 'eventDate': return p.eventDate ? new Date(p.eventDate).getTime() : 0;
      case 'warehouseName': return p.warehouseName || '';
      case 'tripCode': return p.tripCode || '';
      case 'loadingListCode': return p.loadingListCode || '';
      case 'pickingStatus': return this.getPickingStatusLabel(p.pickingStatus);
      default: return '';
    }
  }

  private statusListDisplayValue(p: PickingParcel, field: string): string {
    switch (field) {
      case 'parcelCode': return p.parcelCode || p.parcelBarcode || '';
      case 'eventDate': return this.formatFilterDay(p.eventDate);
      case 'warehouseName': return p.warehouseName || '';
      case 'tripCode': return p.tripCode || '';
      case 'loadingListCode': return p.loadingListCode || '';
      case 'pickingStatus': return this.getPickingStatusLabel(p.pickingStatus);
      default: return '';
    }
  }

  getStatusListValueOptions(field: string): string[] {
    const values = new Set<string>();
    for (const p of this.getStatusListParcels()) {
      const v = this.statusListDisplayValue(p, field);
      if (v) values.add(v);
    }
    let options = Array.from(values).sort((a, b) => this.compareFilterOptions(a, b));
    const search = this.statusListValueSearch[field]?.trim().toUpperCase();
    if (search) {
      options = options.filter(o => o.toUpperCase().includes(search));
    }
    return options;
  }

  isStatusListValueChecked(field: string, value: string): boolean {
    return this.statusListValueFilters[field]?.has(value) ?? false;
  }

  toggleStatusListValue(field: string, value: string): void {
    if (!this.statusListValueFilters[field]) {
      this.statusListValueFilters[field] = new Set<string>();
    }
    const set = this.statusListValueFilters[field];
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
  }

  areAllStatusListValuesChecked(field: string): boolean {
    const options = this.getStatusListValueOptions(field);
    const set = this.statusListValueFilters[field];
    return options.length > 0 && !!set && options.every(o => set.has(o));
  }

  toggleAllStatusListValues(field: string): void {
    const options = this.getStatusListValueOptions(field);
    if (!this.statusListValueFilters[field]) {
      this.statusListValueFilters[field] = new Set<string>();
    }
    const set = this.statusListValueFilters[field];
    if (this.areAllStatusListValuesChecked(field)) {
      options.forEach(o => set.delete(o));
    } else {
      options.forEach(o => set.add(o));
    }
  }

  getFilteredSortedStatusListParcels(): PickingParcel[] {
    let result = this.getStatusListParcels();

    for (const key of Object.keys(this.statusListColumnFilters)) {
      const filterValue = this.statusListColumnFilters[key]?.trim();
      if (!filterValue) continue;
      const f = filterValue.toUpperCase();
      result = result.filter(p => this.statusListDisplayValue(p, key).toUpperCase().includes(f));
    }

    for (const key of Object.keys(this.statusListValueFilters)) {
      const set = this.statusListValueFilters[key];
      if (!set || set.size === 0) continue;
      result = result.filter(p => set.has(this.statusListDisplayValue(p, key)));
    }

    if (this.statusListSort.active) {
      const dir = this.statusListSort.direction === 'asc' ? 1 : -1;
      const field = this.statusListSort.active;
      result = [...result].sort((a, b) => {
        const va = this.statusListFieldValue(a, field);
        const vb = this.statusListFieldValue(b, field);
        if (typeof va === 'number' && typeof vb === 'number') {
          return (va - vb) * dir;
        }
        return String(va).localeCompare(String(vb)) * dir;
      });
    }

    return result;
  }

  search(page?: number): void {
    if (!this.hasRequiredAnchorFilter()) {
      this.filterValidationError = true;
      return;
    }
    this.filterValidationError = false;

    this.isLoading = true;
    this.hasSearched = true;

    const requestedPage = page || this.currentPage;

    const body = {
      warehouseCode: this.isSingleWarehouseUser ? this.filterableWarehouses[0].warehouseCode : this.selectedWarehouseCode,
      dateStart: null,
      dateEnd: null,
      tripCode: this.searchTripCode?.trim() || null,
      parcelCode: this.searchParcelCode?.trim() || null,
      waybillCode: this.searchWaybillCode?.trim() || null,
      originWarehouseCode: this.originWarehouseCode || null,
      pickingDateStart: this.pickingDateFromStr || null,
      pickingDateEnd: this.pickingDateToStr || null,
      shuttleArrivalDateStart: this.shuttleArrivalDateFromStr || null,
      shuttleArrivalDateEnd: this.shuttleArrivalDateToStr || null,
      shuttleDepartureDateStart: this.shuttleDepartureDateFromStr || null,
      shuttleDepartureDateEnd: this.shuttleDepartureDateToStr || null,
      statusFilters: this.statusFilterSelection.size ? Array.from(this.statusFilterSelection) : null,
      tripType: this.getTripTypeParam(),
      platformFilter: this.platformFilter?.trim() || null,
      tripCodeFilter: this.tripCodeFilter?.trim() || null,
      carrierFilter: this.carrierFilter?.trim() || null,
      plateFilter: this.plateFilter?.trim() || null,
      platformValues: this.platformValues.size ? Array.from(this.platformValues) : null,
      tripValues: this.tripValues.size ? Array.from(this.tripValues) : null,
      carrierValues: this.carrierValues.size ? Array.from(this.carrierValues) : null,
      plateValues: this.plateValues.size ? Array.from(this.plateValues) : null,
      tripOriginFilter: this.tripOriginFilter?.trim() || null,
      tripOriginValues: this.tripOriginValues.size ? Array.from(this.tripOriginValues) : null,
      tripArrivalFilter: this.tripArrivalFilter?.trim() || null,
      tripArrivalValues: this.tripArrivalValues.size ? Array.from(this.tripArrivalValues) : null,
      topSortBy: this.topSort.active,
      topSortDirection: this.topSort.direction,
      page: requestedPage,
      pageSize: this.itemsPerPage
    };

    this.http.post<PickingSearchResponse>(`${environment.apiUrl}/overview-picking/search`, body)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => {
          this.summary = { ...EMPTY_SUMMARY, ...(res?.summary || {}) };
          this.groups = res?.groups || [];
          this.totalRecords = res?.totalRecords || 0;
          this.currentPage = res?.page || requestedPage;
          this.itemsPerPage = res?.pageSize || this.itemsPerPage;
          this.serverTripOriginOptions = res?.tripOriginOptions ?? null;
          this.serverTripArrivalDayOptions = res?.tripArrivalDayOptions ?? null;

          if (!this.showPlatformGrouping && this.groups.length > 0) {
            this.expandedWarehouses.add(this.groups[0].warehouseCode);
          }
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(`Errore nella ricerca: ${message}`);
        }
      });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.getTotalPages() || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.search(page);
  }

  setItemsPerPage(size: number): void {
    if (!this.pageSizeOptions.includes(size)) {
      return;
    }
    this.itemsPerPage = size;
    this.currentPage = 1;
    this.search();
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.itemsPerPage));
  }

  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  trackByPageNumber(index: number, page: number): number {
    return page;
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
  }

  toggleReportCollapsed(): void {
    this.isReportCollapsed = !this.isReportCollapsed;
  }

  isWarehouseExpanded(code: string): boolean {
    return this.expandedWarehouses.has(code);
  }

  toggleWarehouseExpand(code: string): void {
    if (this.expandedWarehouses.has(code)) {
      this.expandedWarehouses.delete(code);
    } else {
      this.expandedWarehouses.add(code);
    }
  }

  private tripKey(warehouseCode: string, trip: PickingTripGroup): string {
    return `${warehouseCode}::${trip.tripId ?? 'no-trip'}::${trip.tripCode}`;
  }

  isTripExpanded(warehouseCode: string, trip: PickingTripGroup): boolean {
    return this.expandedTrips.has(this.tripKey(warehouseCode, trip));
  }

  toggleTripExpand(warehouseCode: string, trip: PickingTripGroup): void {
    const key = this.tripKey(warehouseCode, trip);
    if (this.expandedTrips.has(key)) {
      this.expandedTrips.delete(key);
    } else {
      this.expandedTrips.add(key);
    }
  }

  private loadingListKey(warehouseCode: string, trip: PickingTripGroup, ll: PickingLoadingListGroup): string {
    return `${warehouseCode}::${trip.tripId ?? 'no-trip'}::${ll.loadingListId ?? 'no-ll'}::${ll.loadingListCode}`;
  }

  isLoadingListExpanded(warehouseCode: string, trip: PickingTripGroup, ll: PickingLoadingListGroup): boolean {
    return this.expandedLoadingLists.has(this.loadingListKey(warehouseCode, trip, ll));
  }

  toggleLoadingListExpand(warehouseCode: string, trip: PickingTripGroup, ll: PickingLoadingListGroup): void {
    const key = this.loadingListKey(warehouseCode, trip, ll);
    if (this.expandedLoadingLists.has(key)) {
      this.expandedLoadingLists.delete(key);
    } else {
      this.expandedLoadingLists.add(key);
    }
  }

  getLoadingListDirectionLabel(ll: PickingLoadingListGroup): string {
    if (ll.direction === 'DEPARTURE') return 'Carico';
    if (ll.direction === 'ARRIVAL') return 'Scarico';
    return '-';
  }

  trackByLoadingListCode(index: number, ll: PickingLoadingListGroup): string {
    return `${ll.loadingListId ?? 'no-ll'}-${ll.loadingListCode}`;
  }

  openParcelDetail(parcel: PickingParcel, event?: Event): void {
    event?.stopPropagation();
    this.parcelDetailReturnsToStatusList = false;
    this.selectedParcelForModal = parcel;
  }

  openParcelDetailFromStatusList(parcel: PickingParcel, event?: Event): void {
    event?.stopPropagation();
    this.parcelDetailReturnsToStatusList = true;
    this.selectedParcelForModal = parcel;
  }

  closeParcelDetail(): void {
    this.selectedParcelForModal = null;
    this.parcelDetailReturnsToStatusList = false;
    this.statusListPopup = null;
  }

  backFromParcelDetailToStatusList(): void {
    this.selectedParcelForModal = null;
    this.parcelDetailReturnsToStatusList = false;
  }

  get allOrphanParcels(): PickingParcel[] {
    return this.groups.reduce((acc: PickingParcel[], g) => acc.concat(g.orphanParcels || []), []);
  }

  openOrphansListPopup(): void {
    this.showOrphansListPopup = true;
  }

  closeOrphansListPopup(): void {
    this.showOrphansListPopup = false;
  }

  private getAllLoadedParcelsFlat(): PickingParcel[] {
    const result: PickingParcel[] = [];
    for (const g of this.groups) {
      for (const t of g.trips || []) {
        for (const ll of t.loadingLists || []) {
          result.push(...(ll.parcels || []));
        }
      }
    }
    return result;
  }

  getStatusListParcels(): PickingParcel[] {
    if (!this.statusListPopup) return [];
    if (this.statusListPopup === 'ALL') return this.getAllLoadedParcelsFlat();
    return this.getAllLoadedParcelsFlat().filter(p => p.pickingStatus === this.statusListPopup);
  }

  openStatusListPopup(status: PickingStatus | 'ALL'): void {
    this.statusListPopup = status;
  }

  closeStatusListPopup(): void {
    this.statusListPopup = null;
  }

  getStatusListTitle(): string {
    switch (this.statusListPopup) {
      case 'PRE_PLANNED': return 'Colli pre-pickerabili';
      case 'PLANNED': return 'Colli pickerabili';
      case 'EXECUTED': return 'Colli pickerati';
      case 'UNKNOWN_UNCERTAIN': return 'Colli non pickerati incerti';
      case 'UNKNOWN_DEFINITIVE': return 'Colli non pickerati definitivi';
      case 'ALL': return 'Tutti i colli';
      default: return '';
    }
  }

  getStatusListColorClass(): string {
    switch (this.statusListPopup) {
      case 'PRE_PLANNED': return 'picking-popup--cyan';
      case 'PLANNED': return 'picking-popup--orange';
      case 'EXECUTED': return 'picking-popup--green';
      case 'UNKNOWN_UNCERTAIN': return 'picking-popup--yellow';
      case 'UNKNOWN_DEFINITIVE': return 'picking-popup--red';
      case 'ALL': return 'picking-popup--blue';
      default: return '';
    }
  }

  getStatusListIcon(): string {
    switch (this.statusListPopup) {
      case 'ALL': return 'fas fa-boxes-stacked';
      case null: return 'fas fa-list';
      default: return this.getPickingStatusIcon(this.statusListPopup);
    }
  }

  /** Totale colli (somma dei 5 stati, esclusi i pickerati senza affido). */
  get summaryTotal(): number {
    const s = this.summary;
    return (s.totalPrePlanned || 0) + (s.totalPlanned || 0) + (s.totalExecuted || 0)
      + (s.totalUnknownUncertain || 0) + (s.totalUnknownDefinitive || 0);
  }

  getSummaryCount(status: PickingStatus): number {
    switch (status) {
      case 'PRE_PLANNED': return this.summary.totalPrePlanned || 0;
      case 'PLANNED': return this.summary.totalPlanned || 0;
      case 'EXECUTED': return this.summary.totalExecuted || 0;
      case 'UNKNOWN_UNCERTAIN': return this.summary.totalUnknownUncertain || 0;
      case 'UNKNOWN_DEFINITIVE': return this.summary.totalUnknownDefinitive || 0;
    }
  }

  getPickingStatusIcon(status: string | undefined): string {
    switch (status) {
      case 'PRE_PLANNED': return 'fas fa-truck-moving';
      case 'PLANNED': return 'fas fa-hourglass-half';
      case 'EXECUTED': return 'fas fa-circle-check';
      case 'UNKNOWN_UNCERTAIN': return 'fas fa-circle-question';
      case 'UNKNOWN_DEFINITIVE': return 'fas fa-triangle-exclamation';
      default: return 'fas fa-circle';
    }
  }

  /** Suffisso CSS per stato (usato da badge, chip filtro, icone metriche). */
  getPickingStatusKey(status: string | undefined): string {
    switch (status) {
      case 'PRE_PLANNED': return 'preplanned';
      case 'PLANNED': return 'planned';
      case 'EXECUTED': return 'executed';
      case 'UNKNOWN_UNCERTAIN': return 'uncertain';
      case 'UNKNOWN_DEFINITIVE': return 'definitive';
      default: return 'unknown';
    }
  }

  getPickingStatusMetricIconClass(status: PickingStatus): string {
    switch (status) {
      case 'PRE_PLANNED': return 'sm-metric-icon--cyan';
      case 'PLANNED': return 'sm-metric-icon--orange';
      case 'EXECUTED': return 'sm-metric-icon--green';
      case 'UNKNOWN_UNCERTAIN': return 'sm-metric-icon--yellow';
      case 'UNKNOWN_DEFINITIVE': return 'sm-metric-icon--red';
    }
  }

  getPickingStatusTooltip(status: PickingStatus): string {
    switch (status) {
      case 'PRE_PLANNED': return 'Colli caricati in arrivo, data arrivo navetta non ancora disponibile';
      case 'PLANNED': return 'Colli pronti per il picking';
      case 'EXECUTED': return 'Colli pickerati';
      case 'UNKNOWN_UNCERTAIN': return 'Non pickerati senza eventi successivi: probabilmente il collo non era fisicamente presente';
      case 'UNKNOWN_DEFINITIVE': return 'Non pickerati ma con eventi successivi (presa in carico / consegna): il picking andava fatto';
    }
  }

  trackByStatus(index: number, status: PickingStatus): string {
    return status;
  }

  getPickingPercent(executed: number, total: number): number {
    if (!total) {
      return 0;
    }
    return Math.round((executed / total) * 100);
  }

  getPickingStatusLabel(status: string | undefined): string {
    switch (status) {
      case 'PRE_PLANNED': return 'Pre-pickerabile';
      case 'PLANNED': return 'Pickerabile';
      case 'EXECUTED': return 'Pickerato';
      case 'UNKNOWN_UNCERTAIN': return 'Non Pickerato Incerto';
      case 'UNKNOWN_DEFINITIVE': return 'Non Pickerato Definitivo';
      default: return '-';
    }
  }

  /** Etichetta plurale, usata per card riepilogo, chip filtro e intestazioni tabella. */
  getPickingStatusPluralLabel(status: PickingStatus): string {
    switch (status) {
      case 'PRE_PLANNED': return 'Pre-pickerabili';
      case 'PLANNED': return 'Pickerabili';
      case 'EXECUTED': return 'Pickerati';
      case 'UNKNOWN_UNCERTAIN': return 'Non Pickerati Incerti';
      case 'UNKNOWN_DEFINITIVE': return 'Non Pickerati Definitivi';
    }
  }

  getPickingStatusBadgeClass(status: string | undefined): string {
    return `picking-badge picking-badge--${this.getPickingStatusKey(status)}`;
  }

  copyToClipboard(value: string | null | undefined, label: string): void {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      this.notificationService.success(`${label.charAt(0).toUpperCase() + label.slice(1)} copiato: ${value}`);
    });
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  /**
   * Data troncata al giorno (gg/mm/aaaa) usata dai filtri a imbuto delle colonne data:
   * cosi' si seleziona l'intera giornata invece del singolo orario. Stringa vuota se assente.
   */
  formatFilterDay(value: string | null | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // Ordina le opzioni dei filtri: le date gg/mm/aaaa in ordine cronologico, il resto alfabetico
  private compareFilterOptions(a: string, b: string): number {
    const re = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const ma = re.exec(a);
    const mb = re.exec(b);
    if (ma && mb) {
      return `${ma[3]}${ma[2]}${ma[1]}`.localeCompare(`${mb[3]}${mb[2]}${mb[1]}`);
    }
    return a.localeCompare(b);
  }

  formatKg(value: number | null | undefined): string {
    if (value == null) return '0 kg';
    return `${value.toLocaleString('it-IT', { maximumFractionDigits: 2 })} kg`;
  }

  formatM3(value: number | null | undefined): string {
    if (value == null) return '0 m³';
    return `${value.toLocaleString('it-IT', { maximumFractionDigits: 3 })} m³`;
  }

  toYyyyMmDd(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private dateInputFor(field: PickingDateField): HTMLInputElement | undefined {
    switch (field) {
      case 'pickingDateFromStr': return this.pickingDateFromInput?.nativeElement;
      case 'pickingDateToStr': return this.pickingDateToInput?.nativeElement;
      case 'shuttleArrivalDateFromStr': return this.shuttleArrivalDateFromInput?.nativeElement;
      case 'shuttleArrivalDateToStr': return this.shuttleArrivalDateToInput?.nativeElement;
      case 'shuttleDepartureDateFromStr': return this.shuttleDepartureDateFromInput?.nativeElement;
      case 'shuttleDepartureDateToStr': return this.shuttleDepartureDateToInput?.nativeElement;
    }
  }

  getFormattedDate(field: PickingDateField): string {
    const value = this[field];
    if (!value) {
      return '';
    }
    const [year, month, day] = value.split('-');
    return day && month && year ? `${day}/${month}/${year}` : value;
  }

  openDatePicker(field: PickingDateField): void {
    let picker = this.datePickers.get(field);

    if (!picker) {
      const input = this.dateInputFor(field);
      if (!input) {
        return;
      }
      picker = flatpickr(input, {
        dateFormat: 'd/m/Y',
        allowInput: false,
        clickOpens: false,
        locale: Italian,
        defaultDate: this.fromIsoDay(this[field]) || undefined,
        onChange: (selectedDates: Date[]) => {
          this[field] = selectedDates.length ? this.toIsoDay(selectedDates[0]) : '';
          this.datePickers.get(field)?.close();
        },
        onClose: () => {
          this.dateInputFor(field)?.blur();
        }
      });
      this.datePickers.set(field, picker);
    }

    if (!picker.isOpen) {
      if (this[field]) {
        picker.setDate(this.fromIsoDay(this[field])!, false);
      }
      picker.open();
    }
  }

  shiftDateField(field: PickingDateField, days: number): void {
    const base = this.fromIsoDay(this[field]) || new Date();
    if (this[field]) {
      base.setDate(base.getDate() + days);
    }
    this.setDateField(field, this.toIsoDay(base));
  }

  /** Imposta la data di oggi e apre subito il calendario posizionato su oggi. */
  setDateFieldToday(field: PickingDateField): void {
    this.setDateField(field, this.toIsoDay(new Date()));
    // setTimeout: apre il calendario dopo che il click sul pulsante e' terminato,
    // evitando che flatpickr lo interpreti come click esterno e lo richiuda subito
    setTimeout(() => this.openDatePicker(field));
  }

  clearDateField(field: PickingDateField): void {
    this[field] = '';
    this.datePickers.get(field)?.clear();
  }

  private setDateField(field: PickingDateField, value: string): void {
    this[field] = value;
    const date = this.fromIsoDay(value);
    if (date) {
      this.datePickers.get(field)?.setDate(date, false);
    }
  }

  /**
   * Converte 'YYYY-MM-DD' in Date locale (mezzanotte ora locale).
   * Al calendario va passato sempre un Date: la stringa ISO verrebbe interpretata
   * con il dateFormat 'd/m/Y' di flatpickr, producendo una data sbagliata.
   */
  private fromIsoDay(value: string | null | undefined): Date | null {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  private toIsoDay(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  trackByWarehouseCode(index: number, group: PickingWarehouseGroup): string {
    return group.warehouseCode;
  }

  trackByTripCode(index: number, trip: PickingTripGroup): string {
    return `${trip.tripId ?? 'no-trip'}-${trip.tripCode}`;
  }

  trackByParcelCode(index: number, parcel: PickingParcel): string {
    return parcel.parcelCode ?? String(index);
  }

  readonly parcelsPageSize = 5;
  private parcelPageByKey: { [key: string]: number } = {};

  private parcelsPageKey(loadingListCode?: string, tripCode?: string): string {
    return `${tripCode ?? 'no-trip'}::${loadingListCode ?? 'no-ll'}`;
  }

  getParcelsPage(loadingListCode?: string, tripCode?: string): number {
    return this.parcelPageByKey[this.parcelsPageKey(loadingListCode, tripCode)] || 1;
  }

  setParcelsPage(page: number, loadingListCode?: string, tripCode?: string): void {
    this.parcelPageByKey[this.parcelsPageKey(loadingListCode, tripCode)] = page;
  }

  getParcelsTotalPages(parcels: PickingParcel[], loadingListCode?: string, tripCode?: string): number {
    const total = this.getFilteredSortedParcels(parcels).length;
    return Math.max(1, Math.ceil(total / this.parcelsPageSize));
  }

  getPaginatedParcels(parcels: PickingParcel[], loadingListCode?: string, tripCode?: string): PickingParcel[] {
    const filtered = this.getFilteredSortedParcels(parcels);
    const page = Math.min(this.getParcelsPage(loadingListCode, tripCode), Math.max(1, Math.ceil(filtered.length / this.parcelsPageSize)));
    const start = (page - 1) * this.parcelsPageSize;
    return filtered.slice(start, start + this.parcelsPageSize);
  }

  getParcelsPageNumbers(parcels: PickingParcel[], loadingListCode?: string, tripCode?: string): number[] {
    const total = this.getParcelsTotalPages(parcels, loadingListCode, tripCode);
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  goToParcelsPage(page: number, parcels: PickingParcel[], loadingListCode?: string, tripCode?: string): void {
    const total = this.getParcelsTotalPages(parcels, loadingListCode, tripCode);
    if (page < 1 || page > total) return;
    this.setParcelsPage(page, loadingListCode, tripCode);
  }

  getParcelsRangeStart(parcels: PickingParcel[], loadingListCode?: string, tripCode?: string): number {
    const total = this.getFilteredSortedParcels(parcels).length;
    if (total === 0) return 0;
    const page = this.getParcelsPage(loadingListCode, tripCode);
    return (page - 1) * this.parcelsPageSize + 1;
  }

  getParcelsRangeEnd(parcels: PickingParcel[], loadingListCode?: string, tripCode?: string): number {
    const total = this.getFilteredSortedParcels(parcels).length;
    const page = this.getParcelsPage(loadingListCode, tripCode);
    return Math.min(page * this.parcelsPageSize, total);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selectedParcelForModal) {
      this.closeParcelDetail();
      return;
    }
    if (this.showOrphansListPopup) {
      this.closeOrphansListPopup();
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openTopFilterKey = null;
    this.openParcelFilterKey = null;
    this.openOrphanFilterKey = null;
    this.openLoadingListFilterKey = null;
    this.openStatusListFilterKey = null;
  }
}